import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { transportApi, qrApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Bus, MapPin, Pause, Play, SignOut, User, QrCode, ArrowUpRight, ArrowDownRight, CalendarX } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import QrScanner from '../../components/mobile/QrScanner';

/**
 * Driver mobile app (transport_staff role).
 */
const DriverApp = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [routeId, setRouteId] = useState('');
  const [routeDetail, setRouteDetail] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [lastLoc, setLastLoc] = useState(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastSentRef = useRef(0);
  const lastFixRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanAction, setScanAction] = useState('boarded'); // boarded | alighted
  const [recentScans, setRecentScans] = useState([]);

  useEffect(() => {
    transportApi.listRoutes().then(({ data }) => setRoutes(data)).catch(() => {});
    return () => stopTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!routeId) { setRouteDetail(null); return; }
    transportApi.getRoute(routeId).then(({ data }) => setRouteDetail(data)).catch(() => {});
  }, [routeId]);

  const stopTracking = () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const sendFix = async () => {
    const fix = lastFixRef.current;
    if (!fix || !routeId) return;
    try {
      await transportApi.pushLocation({
        route_id: routeId,
        lat: fix.coords.latitude,
        lng: fix.coords.longitude,
        speed: fix.coords.speed != null ? fix.coords.speed * 3.6 : null,
        heading: fix.coords.heading,
      });
      lastSentRef.current = Date.now();
      setLastLoc({ lat: fix.coords.latitude, lng: fix.coords.longitude, ts: new Date() });
    } catch (e) {
      // Silent: keep retrying on next interval
    }
  };

  const startTracking = () => {
    if (!routeId) { toast.error(t('pickRouteFirst')); return; }
    if (!('geolocation' in navigator)) { toast.error(t('geoNotSupported')); return; }
    setTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => { lastFixRef.current = pos; },
      (err) => { toast.error('GPS: ' + (err.message || t('error'))); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    intervalRef.current = setInterval(sendFix, 10000);
    setTimeout(sendFix, 1500);
    toast.success(t('trackingStarted'));
  };

  const stop = () => {
    stopTracking();
    setTracking(false);
    toast.info(t('trackingStopped'));
  };

  const doLogout = async () => { await logout(); navigate('/m/login', { replace: true }); };

  const handleScan = async (token) => {
    setScanning(false);
    if (!routeId) { toast.error(t('pickRouteFirst')); return; }
    try {
      const { data } = await qrApi.transportCheckin(token, routeId, scanAction);
      setRecentScans(prev => [{ ...data, ts: Date.now() }, ...prev].slice(0, 5));
      toast.success(`${scanAction === 'boarded' ? '✓ ' + t('boarded') : '⬇ ' + t('alighted')}: ${data.student_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('unknownQr'));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 pb-24" data-testid="driver-app">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bus size={24} className="text-[#0052FF]" weight="duotone" />
          <div>
            <p className="font-outfit font-bold text-[#0F172A]">{t('driver')}</p>
            <p className="text-xs text-[#64748B]">{user?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/m/my-absences')} data-testid="driver-my-absences" title="Mes absences">
            <CalendarX size={18} className="text-amber-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="driver-logout"><SignOut size={18} /></Button>
        </div>
      </header>

      <Card className="border border-[#E2E8F0] shadow-none mb-4">
        <CardContent className="p-4 space-y-3">
          <label className="text-xs font-medium text-[#64748B] uppercase">{t('myRoute')}</label>
          <select
            className="w-full border border-[#E2E8F0] rounded-md p-3 text-sm"
            value={routeId}
            onChange={e => setRouteId(e.target.value)}
            disabled={tracking}
            data-testid="driver-route-select"
          >
            <option value="">{t('pickRouteShort')}</option>
            {routes.map(r => <option key={r.id} value={r.id}>{r.name} ({r.bus_plate})</option>)}
          </select>

          {tracking ? (
            <Button variant="destructive" className="w-full" onClick={stop} data-testid="driver-stop-btn">
              <Pause size={18} className="mr-2" /> {t('stopTracking')}
            </Button>
          ) : (
            <Button className="w-full bg-[#10B981] hover:bg-[#059669]" onClick={startTracking} disabled={!routeId} data-testid="driver-start-btn">
              <Play size={18} className="mr-2" /> {t('startGpsTracking')}
            </Button>
          )}

          {tracking && (
            <div className="text-xs text-[#64748B] flex items-center gap-2 bg-green-50 p-2 rounded" data-testid="driver-tracking-status">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              {lastLoc
                ? `${t('lastPositionAt')}: ${lastLoc.lat.toFixed(5)}, ${lastLoc.lng.toFixed(5)} — ${lastLoc.ts.toLocaleTimeString()}`
                : t('waitingFirstFix')}
            </div>
          )}
        </CardContent>
      </Card>

      {routeDetail && (
        <Card className="border-2 border-[#0052FF] shadow-none mb-4" data-testid="driver-scan-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <QrCode size={18} className="text-[#0052FF]" weight="duotone" />
              <p className="font-semibold text-sm">{t('qrCheckin')}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={scanAction === 'boarded' ? 'default' : 'outline'}
                onClick={() => setScanAction('boarded')}
                data-testid="driver-action-boarded"
                size="sm"
              >
                <ArrowUpRight size={14} className="mr-1" /> {t('boardAction')}
              </Button>
              <Button
                variant={scanAction === 'alighted' ? 'default' : 'outline'}
                onClick={() => setScanAction('alighted')}
                data-testid="driver-action-alighted"
                size="sm"
              >
                <ArrowDownRight size={14} className="mr-1" /> {t('alightAction')}
              </Button>
            </div>
            {scanning ? (
              <QrScanner onScan={handleScan} onClose={() => setScanning(false)} />
            ) : (
              <Button className="w-full" onClick={() => setScanning(true)} disabled={!routeId} data-testid="driver-scan-btn">
                <QrCode size={18} className="mr-2" /> {t('scanQrButton')}
              </Button>
            )}
            {recentScans.length > 0 && (
              <ul className="space-y-1 text-xs" data-testid="driver-recent-scans">
                {recentScans.map((s, i) => (
                  <li key={`${s.ts}-${i}`} className="flex items-center gap-2 p-1.5 bg-[#F8FAFC] rounded">
                    {s.action === 'boarded' ? <ArrowUpRight size={12} className="text-green-600" /> : <ArrowDownRight size={12} className="text-amber-600" />}
                    <span className="flex-1">{s.student_name}</span>
                    <span className="text-[#94A3B8]">{new Date(s.ts).toLocaleTimeString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {routeDetail && (
        <Card className="border border-[#E2E8F0] shadow-none mb-4">
          <CardContent className="p-4">
            <h2 className="font-outfit font-semibold mb-3 flex items-center gap-2">
              <MapPin size={18} className="text-[#0052FF]" /> {t('stops')}
            </h2>
            <ol className="space-y-1 text-sm">
              {(routeDetail.stops || []).map((st, i) => (
                <li key={i} className="flex items-center gap-2 py-1" data-testid={`driver-stop-${i}`}>
                  <span className="bg-[#0052FF] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{i + 1}</span>
                  <span className="flex-1">{st.name}</span>
                  <span className="text-xs text-[#64748B]">{st.time || ''}</span>
                </li>
              ))}
              {(!routeDetail.stops || routeDetail.stops.length === 0) && <li className="text-xs text-[#94A3B8]">{t('noStops')}</li>}
            </ol>
          </CardContent>
        </Card>
      )}

      {routeDetail && (
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <h2 className="font-outfit font-semibold mb-3 flex items-center gap-2">
              <User size={18} className="text-[#10B981]" /> {t('students_count')} ({(routeDetail.students || []).length})
            </h2>
            <ul className="space-y-1 text-sm">
              {(routeDetail.students || []).map(s => (
                <li key={s.id} className="flex items-center justify-between py-2 border-b border-[#F1F5F9] last:border-0" data-testid={`driver-student-${s.id}`}>
                  <span>{s.name}</span>
                  <span className="text-xs text-[#64748B]">{s.class_name || '—'}</span>
                </li>
              ))}
              {(routeDetail.students || []).length === 0 && <li className="text-xs text-[#94A3B8]">{t('noSubscribedStudents')}</li>}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverApp;
